import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ago, duration, staffApi, staffKeys, usd, useStaffMutation, useStaffQuery } from "@/lib/staff";
import { ErrorLine, Field, StaffOnly, StateBadge } from "@/components/staff/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Page, PageHeader, PageScroll, Panel, PanelBody, PanelHeader, PanelTitle } from "@/components/layout/Page";

export const Route = createFileRoute("/staff/extraction/")({
  component: () => (
    <StaffOnly>
      <Overview />
    </StaffOnly>
  ),
});

/**
 * Every partner the pipeline feeds, and whether any of them needs a person:
 * dead or blocked documents, failing sources or keys, work that has waited
 * too long.
 */
function Overview() {
  const overview = useStaffQuery(staffKeys.overview, staffApi.overview, { refetch: 30_000 });
  const workers = useStaffQuery(["staff", "workers"], staffApi.workers, { refetch: 30_000 });
  const [creating, setCreating] = useState(false);
  const partners = overview.data?.partners ?? [];
  const alive = (workers.data?.workers ?? []).filter((w) => w.alive);

  return (
    <Page>
      <PageHeader
        title="Document pipeline"
        description="Where each partner's documents come from, which models and prompts extract them, which keys pay, and what happened. Staff only."
        actions={
          <>
            <Button asChild variant="secondary">
              <Link to="/staff/extraction/$partnerId" params={{ partnerId: "platform" }}>
                Platform defaults
              </Link>
            </Button>
            <Button variant="primary" onClick={() => setCreating((v) => !v)}>
              {creating ? "Cancel" : "New partner"}
            </Button>
          </>
        }
      />
      <PageScroll className="flex flex-col gap-4">
        {creating && <NewPartner onDone={() => setCreating(false)} />}
        <Panel className="flex-none">
          <PanelHeader>
            <PanelTitle>Partners</PanelTitle>
          </PanelHeader>
          <PanelBody>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Partner</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Library</TableHead>
                  <TableHead className="text-right">Sources</TableHead>
                  <TableHead className="text-right">In flight</TableHead>
                  <TableHead className="text-right">Oldest waiting</TableHead>
                  <TableHead className="text-right">Needs a person</TableHead>
                  <TableHead className="text-right">Published</TableHead>
                  <TableHead className="text-right">Spend 24h / 30d</TableHead>
                  <TableHead>Last poll</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {partners.length === 0 && (
                  <TableEmpty colSpan={10} message={overview.isLoading ? "Loading…" : "No partners yet."} />
                )}
                {partners.map((p) => {
                  const attention = p.dead + p.blocked + p.failed_sources + p.failing_credentials;
                  return (
                    <TableRow key={p.id}>
                      <TableCell>
                        <Link to="/staff/extraction/$partnerId" params={{ partnerId: p.id }} className="font-medium hover:underline">
                          {p.name}
                        </Link>
                        <div className="text-xs text-muted-foreground">{p.slug}</div>
                      </TableCell>
                      <TableCell>
                        <StateBadge state={p.status} title={p.status_reason ?? undefined} />
                      </TableCell>
                      <TableCell className="text-xs">
                        {p.deployment === "self_hosted" ? "self-hosted · " : ""}
                        {p.org_slug ?? new URL(p.ingest_url).host}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{p.sources}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.in_flight}</TableCell>
                      <TableCell className="text-right tabular-nums">{duration(p.oldest_waiting_seconds)}</TableCell>
                      <TableCell className="text-right">
                        {attention === 0 ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <span className="inline-flex flex-wrap justify-end gap-1">
                            {p.dead > 0 && <StateBadge state="dead" title={`${p.dead} dead`} />}
                            {p.blocked > 0 && <StateBadge state="blocked" title={`${p.blocked} blocked`} />}
                            {p.failed_sources > 0 && <StateBadge state="failed" title="a source's last poll failed" />}
                            {p.failing_credentials > 0 && <StateBadge state="failing" title="a key is failing" />}
                            <span className="tabular-nums text-danger-text">{attention}</span>
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{p.items.done ?? 0}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {usd(p.cost_usd_24h)} / {usd(p.cost_usd_30d)}
                      </TableCell>
                      <TableCell className="text-xs">{ago(p.last_run_at)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </PanelBody>
        </Panel>

        <Panel className="flex-none">
          <PanelHeader>
            <PanelTitle>Workers</PanelTitle>
            <span className="text-xs text-muted-foreground">
              {alive.length} alive{workers.data && workers.data.workers.length > alive.length
                ? ` · ${workers.data.workers.length - alive.length} stopped or silent` : ""}
            </span>
          </PanelHeader>
          <PanelBody>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Worker</TableHead>
                  <TableHead>Roles</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Heartbeat</TableHead>
                  <TableHead>State</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(workers.data?.workers ?? []).length === 0 && (
                  <TableEmpty colSpan={5} message="No worker has ever checked in. Is the pipeline deployed?" />
                )}
                {(workers.data?.workers ?? []).slice(0, 20).map((w) => (
                  <TableRow key={w.id}>
                    <TableCell className="font-mono text-xs">{w.id}</TableCell>
                    <TableCell className="text-xs">{w.roles.join(", ")}</TableCell>
                    <TableCell className="text-xs">{w.version ?? "—"}</TableCell>
                    <TableCell className="text-xs">{ago(w.heartbeat_at)}</TableCell>
                    <TableCell>
                      <StateBadge state={w.alive ? "ok" : w.stopped_at ? "disabled" : "failing"} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </PanelBody>
        </Panel>
      </PageScroll>
    </Page>
  );
}

function NewPartner({ onDone }: { onDone: () => void }) {
  const orgs = useStaffQuery(["staff", "orgs"], staffApi.orgs);
  const navigate = useNavigate();
  const create = useStaffMutation(staffApi.createPartner);
  const [form, setForm] = useState({
    slug: "", name: "", org_slug: "", deployment: "saas", ingest_url: "https://app.twodogs.ai",
    default_schedule: "30 2 * * *", timezone: "America/New_York", embedding_dimensions: "3072",
  });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  return (
    <Panel className="flex-none">
      <PanelHeader>
        <PanelTitle>New partner</PanelTitle>
      </PanelHeader>
      <div className="grid gap-4 p-4 md:grid-cols-2 xl:grid-cols-4">
        <Field label="Library" hint="The org whose library this partner's documents land in. Empty for a self-hosted partner.">
          <Select
            value={form.org_slug}
            onChange={(e) => {
              const org = orgs.data?.orgs.find((o) => o.slug === e.target.value);
              setForm({ ...form, org_slug: e.target.value, slug: org?.slug ?? form.slug, name: org?.name ?? form.name,
                deployment: e.target.value ? "saas" : form.deployment });
            }}
          >
            <option value="">— none (self-hosted) —</option>
            {(orgs.data?.orgs ?? []).map((o) => (
              <option key={o.id} value={o.slug}>{o.name} ({o.slug})</option>
            ))}
          </Select>
        </Field>
        <Field label="Slug" hint="Use the org's slug.">
          <Input value={form.slug} onChange={set("slug")} placeholder="acme" />
        </Field>
        <Field label="Name">
          <Input value={form.name} onChange={set("name")} />
        </Field>
        <Field label="Deployment">
          <Select value={form.deployment} onChange={set("deployment")}>
            <option value="saas">Our SaaS</option>
            <option value="self_hosted">Self-hosted (their account)</option>
          </Select>
        </Field>
        <Field label="Library URL" hint="Base URL of the deployment documents are published to.">
          <Input value={form.ingest_url} onChange={set("ingest_url")} />
        </Field>
        <Field label="Default schedule" hint="Five-field cron, in the timezone. Sources can override.">
          <Input value={form.default_schedule} onChange={set("default_schedule")} className="font-mono" />
        </Field>
        <Field label="Timezone">
          <Input value={form.timezone} onChange={set("timezone")} />
        </Field>
        <Field label="Vector width" hint="Must equal the deployment's APP_EMBEDDING_DIMENSIONS.">
          <Input type="number" value={form.embedding_dimensions} onChange={set("embedding_dimensions")} />
        </Field>
      </div>
      <div className="flex items-center gap-3 border-t border-border px-4 py-3">
        <Button
          variant="primary"
          disabled={create.isPending || !form.slug || !form.name}
          onClick={() =>
            create.mutate(
              { ...form, org_slug: form.org_slug || undefined, embedding_dimensions: Number(form.embedding_dimensions) },
              {
                onSuccess: (p) => {
                  onDone();
                  void navigate({ to: "/staff/extraction/$partnerId", params: { partnerId: p.id } });
                },
              },
            )
          }
        >
          Create partner
        </Button>
        <ErrorLine error={create.error} />
      </div>
    </Panel>
  );
}
